import React from 'react';
import styled from 'styled-components';

import CardInformation from './CardInformation';

import Headline2 from '@joinforma/ui/components/Headline2';
import Body2 from '@joinforma/ui/components/Body2';
import Select from '@joinforma/ui/components/Select';
import Option from '@joinforma/ui/components/Option';

import IssueCard from './IssueCard';
import EditBillingAddress from './EditBillingAddress';

import { FormaCard, FormaCardMember } from './types';
import { CARD_STATUS, CARD_TYPE } from 'constants/unit';
import { FORMA_CARD_LIST_PAGE_ELEMENTS } from 'constants/testSelectors';

interface Props {
  forma_card_member: FormaCardMember;
  employee_full_name: string;
  employee_id: string;
  cards?: Array<FormaCard>;
  dependents: Array<FormaCardMember>;
  applyFilters: (filters: { id?: string }) => void;
  filtered_id?: string;
  employee_primary_residence: FormaCardMember['billing_address'];
  refetchCards: () => void;
}

const Container = styled.div``;
const CardInformationDropdownContainer = styled.div``;

const Title = styled(Headline2)``;

const Description = styled(Body2)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CardInformationDropdown = styled(Select)`
  width: 100%;
  margin: 32px 0;
`;

const FormaCardList = (props: Props) => {
  const {
    forma_card_member,
    forma_card_member: { first_name },
    employee_full_name,
    employee_id,
    cards,
    filtered_id,
    dependents,
    employee_primary_residence,
    applyFilters,
    refetchCards,
  } = props;

  // Checking if physical cards are inactive, if it is inactive then we display the activate physical card view. Note Unit provides 4 card statuses - active | inactive | closed | frozen
  const active_physical_cards = cards?.filter(
    ({ cardType, cardStatus }: FormaCard) =>
      cardType === CARD_TYPE.PHYSICAL &&
      cardStatus !== CARD_STATUS.INACTIVE &&
      cardStatus !== CARD_STATUS.CLOSED
  );
  const inactive_physical_cards = cards?.filter(
    ({ cardType, cardStatus }: FormaCard) =>
      cardType === CARD_TYPE.PHYSICAL && cardStatus === CARD_STATUS.INACTIVE
  );

  const physical_card =
    active_physical_cards?.[0] || inactive_physical_cards?.[0];

  return (
    <Container data-testid={FORMA_CARD_LIST_PAGE_ELEMENTS.CONTAINER}>
      <CardInformationDropdownContainer>
        <Title>Forma Card</Title>
        <Description>
          Use your Forma card to spend your benefit funds without needing to
          file a claim to get reimbursed
        </Description>
        <CardInformationDropdown
          label="View card information for"
          name="id"
          value={filtered_id}
          placeholder="View card information for"
          onChange={(id) => {
            if (typeof id === 'string') {
              applyFilters({
                id,
              });
            }
          }}
        >
          <Option key={employee_id} value={employee_id}>
            {`${employee_full_name} (myself)`}
          </Option>
          {dependents?.map((dependent) => (
            <Option key={dependent.id} value={dependent.id}>
              {`${dependent.first_name} ${dependent.last_name}`}
            </Option>
          ))}
        </CardInformationDropdown>
      </CardInformationDropdownContainer>

      {cards?.length ? (
        <Container>
          <CardInformation
            active_physical_cards={active_physical_cards}
            inactive_physical_cards={inactive_physical_cards}
            forma_card_member={forma_card_member}
            employee_id={employee_id}
            refetchCards={refetchCards}
          />
          {physical_card?.cardHolderDetails?.billingAddress && (
            <EditBillingAddress
              physical_card_address={
                physical_card.cardHolderDetails.billingAddress
              }
              physical_card_id={physical_card.cardId}
              first_name={first_name}
              refetchCards={refetchCards}
            />
          )}
        </Container>
      ) : (
        <IssueCard
          forma_card_member={forma_card_member}
          employee_id={employee_id}
          mailing_address={employee_primary_residence}
          refetchCards={refetchCards}
        />
      )}
    </Container>
  );
};

export default FormaCardList;
